/**
 * Frog stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const frogStickersPack: ToilePack = {
    slug: 'frog-stickers',
    category: 'sticker',
    name: 'Frog stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pad', label: 'Lily pad', subject: 'a round green frog sitting on a lily pad with a blush'},
        {key: 'hat', label: 'Frog in a hat', subject: 'a frog wearing a tiny knitted hat, looking up'},
        {key: 'mushroom', label: 'Frog and mushroom', subject: 'a frog hugging a red-capped mushroom'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a toad instead', 'for a water bottle', 'in autumn colors'],
    directionHint: 'try: a toad instead',
    seo: {
        title: 'Frog Stickers: Lily Pad, Hat, Mushroom',
        metaDescription: 'Nine frog stickers — on a lily pad, in a tiny hat, hugging a mushroom — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Frog stickers: lily pad, tiny hat, mushroom',
        tagline: 'Nine stickers, three frog moments, three styles each.',
        intro: 'The frog of the sticker economy, in its three signature moments: round on a lily pad, wearing a tiny knitted hat, and hugging a red-capped mushroom. Each one in kawaii, bold cartoon, and loose hand-drawn finishes. Ask for a different colour frog, a rainy-day scene, or a whole pond composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a cottagecore planner spread',
            'a phone case sticker sheet',
            "a kids' room decoration",
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine frog stickers — a frog on a lily pad, a frog in a hat, and a frog hugging a mushroom — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a different frog colour or scene?', a: 'Yes — the direction field takes any variation, including a different colour frog, a rainy-day scene, or a whole pond composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Is this a real frog species?', a: 'No — every frog is a rounded cartoon character, not an illustration of a specific real species.'},
        ],
    },
}
